import { useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Image,
  Link2,
  Minus,
  Eye,
  Edit3,
  Type,
} from 'lucide-react';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
}

export function MarkdownEditor({ value, onChange, placeholder, minRows = 20 }: MarkdownEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPreview, setIsPreview] = useState(false);

  const insertAtCursor = useCallback((before: string, after: string = '', defaultText: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || defaultText;
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange(newText);

    // Restore cursor position after React re-render
    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [value, onChange]);

  const insertBlock = useCallback((prefix: string, defaultText: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const selectedText = value.substring(textarea.selectionStart, textarea.selectionEnd) || defaultText;
    
    // Ensure we're on a new line
    const beforeCursor = value.substring(0, start);
    const needsNewline = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
    const needsExtraNewline = beforeCursor.length > 0 && !beforeCursor.endsWith('\n\n');
    
    let insert = '';
    if (needsExtraNewline) insert += '\n\n';
    else if (needsNewline) insert += '\n';
    
    insert += prefix + selectedText + '\n\n';
    
    const newText = beforeCursor + insert + value.substring(textarea.selectionEnd);
    onChange(newText);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = beforeCursor.length + insert.length - 1;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [value, onChange]);

  const toolbarActions = [
    {
      icon: <Heading1 className="h-4 w-4" />,
      title: 'Heading 1',
      action: () => insertBlock('# ', 'Heading 1'),
    },
    {
      icon: <Heading2 className="h-4 w-4" />,
      title: 'Heading 2',
      action: () => insertBlock('## ', 'Heading 2'),
    },
    {
      icon: <Heading3 className="h-4 w-4" />,
      title: 'Heading 3',
      action: () => insertBlock('### ', 'Heading 3'),
    },
    { separator: true },
    {
      icon: <Bold className="h-4 w-4" />,
      title: 'Bold',
      action: () => insertAtCursor('**', '**', 'bold text'),
    },
    {
      icon: <Italic className="h-4 w-4" />,
      title: 'Italic',
      action: () => insertAtCursor('*', '*', 'italic text'),
    },
    { separator: true },
    {
      icon: <List className="h-4 w-4" />,
      title: 'Bullet List',
      action: () => insertBlock('- ', 'List item'),
    },
    {
      icon: <ListOrdered className="h-4 w-4" />,
      title: 'Numbered List',
      action: () => insertBlock('1. ', 'List item'),
    },
    { separator: true },
    {
      icon: <Quote className="h-4 w-4" />,
      title: 'Blockquote',
      action: () => insertBlock('> ', 'Quote text'),
    },
    {
      icon: <Code className="h-4 w-4" />,
      title: 'Code Block',
      action: () => insertAtCursor('\n\n```\n', '\n```\n\n', 'code here'),
    },
    {
      icon: <Minus className="h-4 w-4" />,
      title: 'Horizontal Rule',
      action: () => insertBlock('---\n', ''),
    },
    { separator: true },
    {
      icon: <Link2 className="h-4 w-4" />,
      title: 'Link',
      action: () => {
        const url = prompt('Enter URL:');
        if (url) insertAtCursor('[', `](${url})`, 'link text');
      },
    },
    {
      icon: <Image className="h-4 w-4" />,
      title: 'Image',
      action: () => {
        const url = prompt('Enter image URL:');
        if (url) {
          const alt = prompt('Enter alt text:', 'Image description');
          insertBlock(`![${alt || 'Image'}](${url})`, '');
        }
      },
    },
  ];

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-2 bg-muted/50 border-b flex-wrap">
        {toolbarActions.map((action, i) =>
          'separator' in action ? (
            <Separator key={i} orientation="vertical" className="h-6 mx-1" />
          ) : (
            <Button
              key={i}
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title={action.title}
              onClick={action.action}
              disabled={isPreview}
            >
              {action.icon}
            </Button>
          )
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant={!isPreview ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setIsPreview(false)}
          >
            <Edit3 className="h-3.5 w-3.5" /> Write
          </Button>
          <Button
            type="button"
            variant={isPreview ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setIsPreview(true)}
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </Button>
        </div>
      </div>

      {/* Editor / Preview */}
      {isPreview ? (
        <div className="p-6 min-h-[400px] overflow-auto prose prose-lg dark:prose-invert max-w-none
          prose-headings:font-black prose-headings:tracking-tight
          prose-h1:text-3xl prose-h1:mt-8 prose-h1:mb-4
          prose-h2:text-2xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:border-b prose-h2:border-zinc-200 prose-h2:pb-2
          prose-h3:text-xl prose-h3:mt-4 prose-h3:mb-2
          prose-p:leading-relaxed prose-p:mb-4
          prose-ul:list-disc prose-ul:ml-6 prose-ul:my-4
          prose-ol:list-decimal prose-ol:ml-6 prose-ol:my-4
          prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:p-4 prose-blockquote:rounded-r-xl prose-blockquote:italic
          prose-code:bg-zinc-100 dark:prose-code:bg-zinc-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
          prose-img:rounded-2xl prose-img:shadow-lg prose-img:my-6
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline
          prose-hr:my-8
        ">
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-muted-foreground italic">Nothing to preview yet. Start writing!</p>
          )}
        </div>
      ) : (
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Write your blog post content here using Markdown...\n\n# Heading 1\n## Heading 2\n\nParagraph text here.\n\n- Bullet point\n- Another point\n\n**Bold text** and *italic text*\n\n> Blockquote\n\n![Image alt](https://example.com/image.jpg)"}
          rows={minRows}
          className="border-0 rounded-none focus-visible:ring-0 font-mono text-sm resize-y min-h-[400px]"
        />
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30 border-t text-[11px] text-muted-foreground">
        <span>Markdown supported • Use toolbar or type markdown syntax directly</span>
        <span>{value.length} chars • ~{Math.ceil(value.split(/\s+/).filter(Boolean).length / 200)} min read</span>
      </div>
    </div>
  );
}
